<template>
  <div class="catalog-root">
    <!-- Hidden debug trigger: tap top-left corner -->
    <div class="debug-tap-zone" @click="showInfo = !showInfo"></div>

    <!-- ══ Header ══════════════════════════════════════════════════════════ -->
    <header class="catalog-header">
      <div class="header-right">
        <button class="cart-btn" @click="showCart = true" title="View cart">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
          <span v-if="cart.length" class="cart-badge">{{ cart.length }}</span>
        </button>
      </div>
    </header>

    <!-- ══ Debug / Info Overlay ═════════════════════════════════════════════ -->
    <transition name="fade">
      <div v-if="showInfo" class="info-overlay" @click.self="showInfo = false">
        <div class="info-card">
          <div class="info-card-header">
            <span>Terminal Debug</span>
            <button class="overlay-close" @click="showInfo = false">X</button>
          </div>
          <div class="info-row">
            <span class="info-lbl">Backend</span>
            <span :class="wsConnected ? 'val-ok' : 'val-err'">
              {{ wsConnected ? 'Connected' : 'Disconnected' }}
            </span>
          </div>
          <div class="info-row">
            <span class="info-lbl">Retail ID</span>
            <span class="info-val">{{ retailId }}</span>
          </div>
          <div class="info-row">
            <span class="info-lbl">Last refresh</span>
            <span class="info-val">{{ lastUpdated || '—' }}</span>
          </div>
          <div class="info-row">
            <span class="info-lbl">SALEABLE total</span>
            <span class="info-val">{{ totalSaleable }}</span>
          </div>
          <div class="info-row">
            <span class="info-lbl">In cart</span>
            <span class="info-val">{{ cart.length }}</span>
          </div>
          <div class="info-row">
            <span class="info-lbl">Games shown</span>
            <span class="info-val">{{ gameList.length }}</span>
          </div>
          <div class="info-divider"></div>
          <div
            v-for="game in gameList"
            :key="game.type"
            class="info-row info-row-sm"
          >
            <span class="info-lbl">{{ game.type }}</span>
            <span class="info-val"
              >{{ game.tickets.length }} avail ·
              {{ formatPrice(game.price) }}</span
            >
          </div>
          <div class="info-divider"></div>
          <div v-if="fetchError" class="info-error">{{ fetchError }}</div>
          <button class="info-refresh-btn" @click="fetchAll">
            Refresh now
          </button>
        </div>
      </div>
    </transition>

    <!-- ══ Game Detail Modal ════════════════════════════════════════════════ -->
    <transition name="modal-pop">
      <div v-if="selected" class="game-modal-backdrop" @click.self="selectedType = null">
        <div class="game-modal-card">
          <button class="expanded-close" @click="selectedType = null">X</button>

          <div class="game-modal-body">
            <div class="expanded-img-col">
              <img
                v-if="selected.layout || selected.card"
                class="expanded-img"
                :src="selected.layout || selected.card"
                alt="Game layout"
              />
              <div v-else class="expanded-img-placeholder">[IMG]</div>
            </div>

            <div class="expanded-detail-col">
              <h2 class="expanded-name">{{ gameNames[selected.type] || selected.name }}</h2>
              <p class="expanded-price">${{ formatPrice(selected.price) }}</p>
              <button
                class="btn-add-to-cart"
                :disabled="!selected.tickets.length"
                @click="addToCart(selected)"
              >
                {{ selected.tickets.length ? '+ Add to Cart' : 'Sold Out' }}
              </button>
              <p v-if="cartMsg" class="cart-feedback">{{ cartMsg }}</p>
              <p class="expanded-avail-count">{{ selected.tickets.length }} available</p>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ Cart Backdrop ══════════════════════════════════════════════════════ -->
    <div v-if="showCart" class="cart-backdrop" @click="showCart = false"></div>

    <!-- ══ Cart Panel ════════════════════════════════════════════════════════ -->
    <transition name="slide-right">
      <div v-if="showCart" class="cart-panel">
        <div class="cart-header">
          <span class="cart-title">Cart ({{ cart.length }})</span>
          <button class="overlay-close" @click="showCart = false">X</button>
        </div>

        <div class="cart-items">
          <div v-if="!cart.length" class="cart-empty">
            No items yet — add games from the catalog
          </div>
          <div v-for="group in cartGrouped" :key="group.gameType" class="cart-item">
            <img
              v-if="group.card || group.layout"
              :src="group.card || group.layout"
              class="cart-item-thumb"
              alt=""
            />
            <div v-else class="cart-item-thumb-placeholder">[IMG]</div>
            <div class="cart-item-info">
              <div class="cart-item-name">{{ group.name }}</div>
              <div class="cart-item-price">{{ formatPrice(group.price) }} each</div>
            </div>
            <div class="cart-item-qty-controls">
              <button
                class="cart-remove-btn"
                @click="removeOneFromCart(group.gameType)"
                title="Remove one"
              >
                −
              </button>
              <span class="cart-item-qty">{{ group.tickets.length }}</span>
              <button
                class="cart-add-btn"
                @click="addOneToCart(group.gameType)"
                title="Add one"
              >
                +
              </button>
            </div>
          </div>
        </div>

        <div class="cart-footer">
          <div class="cart-total-row">
            <span class="cart-total-lbl">Total</span>
            <span class="cart-total-val">{{ cartTotalDisplay }}</span>
          </div>
          <div v-if="checkoutError" class="cart-error">{{ checkoutError }}</div>
          <button
            class="btn-checkout"
            :disabled="!cart.length || paying"
            @click="confirmPayment"
          >
            {{ paying ? 'Processing...' : 'Confirm Payment' }}
          </button>
        </div>
      </div>
    </transition>

    <!-- ══ Claim Screen (post-payment) ══════════════════════════════════════ -->
    <div v-if="claimData" class="claim-screen">
      <div class="claim-header claim-print-hide">
        <span class="claim-header-title"
          >Payment Confirmed — Tickets Ready</span
        >
        <button class="overlay-close claim-close-btn" @click="doneClaim">
          X
        </button>
      </div>

      <div class="claim-body">
        <p class="claim-instruction">
          Have the Player Scan the QR Code in the Brightstar app to Redeem their Tickets
        </p>

        <div class="claim-qr-wrap" @click="devQrTap">
          <vue-qrcode
            :value="claimData.qrValue"
            :width="280"
            :margin="3"
            :color="{ dark: '#000000ff', light: '#ffffffff' }"
          />
        </div>

        <template v-if="isDev">
          <p
            v-if="devQrTaps > 0 && devQrTaps < 5"
            class="dev-tap-hint claim-print-hide"
          >
            {{ 5 - devQrTaps }} more tap{{ devQrTaps === 4 ? '' : 's' }} to
            reveal payload
          </p>
          <div v-if="devQrTaps >= 5" class="dev-qr-reveal claim-print-hide">
            <span class="dev-qr-label">[DEV] QR payload</span>
            <code class="dev-qr-code">{{ claimData.qrValue }}</code>
            <button class="dev-copy-btn" @click.stop="devCopyQr">Copy</button>
          </div>
        </template>

        <p class="claim-or-label claim-print-hide">— or —</p>
        <p class="claim-print-label">Print this receipt to claim later</p>

        <div class="claim-ticket-list">
          <div
            v-for="t in claimData.tickets"
            :key="t.id"
            class="claim-ticket-row"
          >
            <span class="claim-ticket-id">{{ t.id }}</span>
            <span class="claim-ticket-price">${{ formatPrice(t.price) }}</span>
          </div>
          <div class="claim-total-row" @click="devReceiptTap">
            <span class="claim-total-lbl">Total</span>
            <span class="claim-total-val">${{ claimTotalDisplay }}</span>
          </div>
        </div>

        <template v-if="isDev">
          <p
            v-if="devReceiptTaps > 0 && devReceiptTaps < 5"
            class="dev-tap-hint claim-print-hide"
          >
            {{ 5 - devReceiptTaps }} more tap{{ devReceiptTaps === 4 ? '' : 's' }} to preview receipt image
          </p>
          <div v-if="devReceiptTaps >= 5" class="dev-receipt-preview claim-print-hide">
            <div class="dev-receipt-header">
              <span class="dev-qr-label">[DEV] Receipt image preview</span>
              <button class="dev-copy-btn" @click.stop="devRegenerateReceipt">Re-generate</button>
            </div>
            <p v-if="devReceiptError" class="dev-receipt-error">{{ devReceiptError }}</p>
            <img
              v-else-if="devReceiptImage"
              :src="devReceiptImage"
              class="dev-receipt-img"
              alt="Receipt preview"
            />
            <p v-else class="dev-tap-hint">Generating…</p>
          </div>
        </template>

        <div class="claim-actions claim-print-hide">
          <button class="btn-print" @click="printTickets">
            Print Receipt
          </button>
          <button class="btn-done-claim" @click="doneClaim">Done</button>
        </div>
      </div>
    </div>

    <!-- ══ Empty / Loading states ═══════════════════════════════════════════ -->
    <div v-if="loading && !claimData" class="state-message">
      <div class="spinner"></div>
      <span>Loading games…</span>
    </div>

    <div
      v-else-if="!loading && gameList.length === 0 && !claimData"
      class="state-message"
    >
      <span>No games available at this terminal.</span>
      <button
        class="info-refresh-btn"
        style="margin-top: 16px"
        @click="fetchAll"
      >
        Retry
      </button>
    </div>

    <!-- ══ Catalog Grid ══════════════════════════════════════════════════════ -->
    <div
      v-if="!loading && gameList.length > 0 && !claimData"
      class="catalog-wrap"
    >
      <div class="catalog-track">
        <div
          v-for="game in gameList"
          :key="game.type"
          class="game-card"
          :class="{ 'card-active': selected && selected.type === game.type }"
          @click="selectGame(game)"
        >
          <div class="card-info-tap" @click.stop="showCardInfo(game)"></div>

          <img
            v-if="game.card || game.layout"
            class="card-img"
            :src="game.card || getImageSrc(game.layout)"
            alt="Game"
          />
          <div v-else class="card-img-placeholder">[IMG]</div>

          <div class="card-bottom-bar">
            <div class="card-bar-spacer"></div>
            <span class="card-price-pill">{{ formatPrice(game.price) }}</span>
            <div class="card-bar-right">
              <span class="card-ticket-count">{{ game.tickets.length }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ Per-card info modal ════════════════════════════════════════════════ -->
    <transition name="fade">
      <div
        v-if="cardInfoGame"
        class="info-overlay"
        @click.self="cardInfoGame = null"
      >
        <div class="info-card">
          <div class="info-card-header">
            <span>{{ cardInfoGame.name }}</span>
            <button class="overlay-close" @click="cardInfoGame = null">
              X
            </button>
          </div>
          <div class="info-row">
            <span class="info-lbl">Type</span>
            <span class="info-mono">{{ cardInfoGame.type }}</span>
          </div>
          <div class="info-row">
            <span class="info-lbl">Price</span>
            <span class="info-val">{{ formatPrice(cardInfoGame.price) }}</span>
          </div>
          <div class="info-row">
            <span class="info-lbl">Available</span>
            <span class="info-val">{{ cardInfoGame.tickets.length }}</span>
          </div>
          <div class="info-divider"></div>
          <div
            v-for="t in cardInfoGame.tickets.slice(0, 10)"
            :key="t.id"
            class="info-row info-row-sm"
          >
            <span class="info-mono" style="font-size: 11px">{{ t.id }}</span>
            <span class="info-val" style="font-size: 11px">{{ t.status }}</span>
          </div>
          <div
            v-if="cardInfoGame.tickets.length > 10"
            class="info-row info-row-sm"
          >
            <span class="info-lbl"
              >…and {{ cardInfoGame.tickets.length - 10 }} more</span
            >
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import VueQrcode from '@chenfengyuan/vue-qrcode'
import restService from '../services/RestService'
import WSServiceUpdateTicket from '../services/WSServiceUpdateTicket'
import WSServiceSyncTicket from '../services/WSServiceSyncTicket'

const PAGE_SIZE = 1000

export default {
  name: 'CatalogView',
  components: { VueQrcode },

  data() {
    return {
      retailId: this.$route.params.retailId || '801000',
      allTickets: [],
      selectedType: null,
      loading: true,
      fetchError: null,
      showInfo: false,
      cardInfoGame: null,
      wsConnected: false,
      lastUpdated: null,
      // Cart
      cart: [],
      showCart: false,
      cartMsg: null,
      paying: false,
      checkoutError: null,
      claimData: null,
      isDev: import.meta.env.VITE_DEV === 'true' || import.meta.env.DEV,
      devQrTaps: 0,
      devReceiptTaps: 0,
      devReceiptImage: null,
      devReceiptError: null,
      gameNames: {},
    }
  },

  computed: {
    gameGroups() {
      const inCart = new Set(this.cart.map((t) => t.id))
      const groups = {}
      for (const t of this.allTickets) {
        if (t.status !== 'SALEABLE' || inCart.has(t.id)) continue
        const key = t.type || String(t.id).split('-')[0] || 'UNKNOWN'
        if (!groups[key]) {
          groups[key] = {
            type: key,
            name: this.formatName(key),
            layout: `/game-image/${key}/layout`,
            card: `/game-image/${key}/card`,
            price: t.price || '',
            tickets: [],
          }
        }
        groups[key].tickets.push(t)
      }
      return groups
    },

    gameList() {
      return Object.values(this.gameGroups)
    },

    // Computed from selectedType so it stays current when cart changes
    selected() {
      return this.selectedType
        ? this.gameGroups[this.selectedType] || null
        : null
    },

    totalSaleable() {
      return this.allTickets.filter((t) => t.status === 'SALEABLE').length
    },

    cartGrouped() {
      const groups = {}
      for (const item of this.cart) {
        if (!groups[item.gameType]) {
          groups[item.gameType] = {
            gameType: item.gameType,
            name: item.name,
            price: item.price,
            card: item.card,
            layout: item.layout,
            tickets: [],
          }
        }
        groups[item.gameType].tickets.push(item)
      }
      return Object.values(groups)
    },

    cartTotal() {
      return this.cart.reduce((sum, t) => sum + this.parsePrice(t.price), 0)
    },

    cartTotalDisplay() {
      return this.cartTotal.toFixed(2)
    },

    claimTotalDisplay() {
      if (!this.claimData) return '0.00'
      const sum = this.claimData.tickets.reduce(
        (s, t) => s + this.parsePrice(t.price),
        0,
      )
      return sum.toFixed(2)
    },
  },

  async mounted() {
    await this.fetchAll()
    this.connectWS()
    try {
      const resp = await fetch('/game-config')
      const data = await resp.json()
      const games = data?.games || {}
      this.gameNames = Object.fromEntries(
        Object.entries(games).map(([id, g]) => [id, g.name || id])
      )
    } catch {
      // game config unavailable — names fall back to formatted type
    }
  },

  beforeUnmount() {
    WSServiceUpdateTicket.disconnect()
    WSServiceSyncTicket.disconnect()
  },

  methods: {
    // ── Data fetching ─────────────────────────────────────────────────────
    async fetchAll() {
      this.loading = true
      this.fetchError = null
      try {
        const first = await restService.getTickets(this.retailId, 1, PAGE_SIZE)
        const total = first.recordsTotal || 0
        const totalPages = Math.ceil(total / PAGE_SIZE)
        let all = first.data || []

        if (totalPages > 1) {
          const rest = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) =>
              restService
                .getTickets(this.retailId, i + 2, PAGE_SIZE)
                .then((r) => r.data || []),
            ),
          )
          all = all.concat(rest.flat())
        }

        this.allTickets = all
        this.lastUpdated = new Date().toLocaleTimeString()

        // Drop cart items no longer SALEABLE (sold at another terminal while in cart)
        const saleableIds = new Set(
          all.filter((t) => t.status === 'SALEABLE').map((t) => t.id),
        )
        this.cart = this.cart.filter((t) => saleableIds.has(t.id))
      } catch (err) {
        this.fetchError = err?.message || 'Failed to load tickets'
      } finally {
        this.loading = false
      }
    },

    // ── WebSocket ─────────────────────────────────────────────────────────
    connectWS() {
      try {
        WSServiceUpdateTicket.connect((notification) => {
          this.wsConnected = true
          try {
            const updated = JSON.parse(notification.body)
            const idx = this.allTickets.findIndex((t) => t.id === updated.id)
            if (idx !== -1) {
              this.allTickets[idx] = { ...this.allTickets[idx], status: updated.status }
            }
            if (
              this.selected &&
              updated.status !== 'SALEABLE' &&
              this.selected.tickets.every(
                (t) => t.id === updated.id || t.status !== 'SALEABLE',
              )
            ) {
              this.selectedType = null
            }
          } catch (_) {}
        }, this.retailId)
        this.wsConnected = true
      } catch (_) {
        this.wsConnected = false
      }

      try {
        WSServiceSyncTicket.connect(() => {
          this.fetchAll()
        }, this.retailId)
      } catch (_) {}
    },

    // ── Cart ──────────────────────────────────────────────────────────────
    addToCart(game) {
      if (!game || !game.tickets.length) {
        this.cartMsg = game
          ? `No more ${game.name} tickets available`
          : 'No tickets available'
        setTimeout(() => {
          this.cartMsg = null
        }, 2500)
        return
      }
      // game.tickets already excludes cart items via gameGroups computed
      const ticket = game.tickets[0]
      const displayName = this.gameNames[game.type] || game.name
      this.cart.push({
        id: ticket.id,
        gameType: game.type,
        name: displayName,
        price: ticket.price,
        layout: game.layout,
        card: game.card,
      })
      this.cartMsg = 'Added'
      setTimeout(() => {
        this.cartMsg = null
      }, 1500)
    },

    removeFromCart(index) {
      this.cart.splice(index, 1)
    },

    removeOneFromCart(gameType) {
      for (let i = this.cart.length - 1; i >= 0; i--) {
        if (this.cart[i].gameType === gameType) {
          this.cart.splice(i, 1)
          break
        }
      }
    },

    addOneToCart(gameType) {
      const game = this.gameGroups[gameType]
      this.addToCart(game || { tickets: [], name: gameType, type: gameType })
    },

    async confirmPayment() {
      if (!this.cart.length) return
      this.paying = true
      this.checkoutError = null
      try {
        for (const ticket of this.cart) {
          // Calls DST → H2 persisted SALEABLE→ON SALE; retail-monitor-be notified via async wire-tap
          await restService.reserveTicket(this.retailId, ticket.id)
        }
        const confirmedIds = new Set(this.cart.map((t) => t.id))
        // Remove from local state immediately so count drops even if WS is slow or
        // the next fetchAll overwrites retail-monitor-be status back to SALEABLE.
        this.allTickets = this.allTickets.filter((t) => !confirmedIds.has(t.id))
        this.claimData = {
          tickets: [...this.cart],
          qrValue: JSON.stringify({ tickets: [...confirmedIds] }),
        }
        this.showCart = false
        this.$nextTick(() => this.generateAndPostReceiptImage())
      } catch (e) {
        this.checkoutError =
          e?.message || 'Payment confirmation failed — please retry'
      } finally {
        this.paying = false
      }
    },

    printTickets() {
      this.generateAndPostReceiptImage()
    },

    async generateAndPostReceiptImage() {
      if (!this.claimData) return

      // Give Vue two ticks so the QR <canvas> is in the DOM
      await this.$nextTick()
      await this.$nextTick()

      const qrCanvas = this.$el.querySelector('.claim-qr-wrap canvas')
      const purchaseTime = new Date().toLocaleString()
      const retailerId = this.$route.params.retailId || ''

      const CANVAS_W = 640
      const MARGIN = 32
      const qrSize = 280
      const headerH = 100
      const qrLabelH = 36
      const qrBlockH = qrSize + 32
      const stepsH = 84
      const dividerH = 24
      const ticketHeaderH = 32
      const ticketRowH = 38
      const totalRowH = 56
      const footerH = 72
      const ticketCount = this.claimData.tickets.length

      const CANVAS_H =
        headerH +
        qrLabelH +
        qrBlockH +
        stepsH +
        dividerH +
        ticketHeaderH +
        ticketCount * ticketRowH +
        8 +
        totalRowH +
        footerH

      const canvas = document.createElement('canvas')
      canvas.width = CANVAS_W
      canvas.height = CANVAS_H
      const ctx = canvas.getContext('2d')

      // White background
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H)

      let y = 0

      // ── Header band ──
      ctx.fillStyle = '#000000'
      ctx.fillRect(0, y, CANVAS_W, headerH)
      ctx.fillStyle = '#ffffff'
      ctx.font = '11px system-ui, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('★  ★  ★  ★  ★  ★  ★  ★  ★  ★  ★  ★  ★  ★  ★', CANVAS_W / 2, y + 18)
      ctx.font = 'bold 24px system-ui, sans-serif'
      ctx.fillText('DIGITAL SCRATCH TICKET RECEIPT', CANVAS_W / 2, y + 52)
      ctx.fillStyle = '#cccccc'
      ctx.font = '13px system-ui, sans-serif'
      ctx.fillText('Brightstar Gaming', CANVAS_W / 2, y + 76)
      y += headerH

      // ── QR label ──
      ctx.fillStyle = '#000000'
      ctx.font = 'bold 13px system-ui, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('SCAN TO REDEEM', CANVAS_W / 2, y + 24)
      y += qrLabelH

      // ── QR code ──
      const qrX = (CANVAS_W - qrSize) / 2
      const qrY = y + 8
      if (qrCanvas) {
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(qrX - 4, qrY - 4, qrSize + 8, qrSize + 8)
        ctx.strokeStyle = '#000000'
        ctx.lineWidth = 1
        ctx.strokeRect(qrX - 4, qrY - 4, qrSize + 8, qrSize + 8)
        ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize)
      }
      y += qrBlockH

      // ── Redemption steps ──
      ctx.strokeStyle = '#dddddd'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(MARGIN, y)
      ctx.lineTo(CANVAS_W - MARGIN, y)
      ctx.stroke()
      y += 8

      const steps = [
        '1.  Open the Brightstar app',
        '2.  Tap "Redeem Tickets"',
        '3.  Scan the QR code above',
      ]
      ctx.textAlign = 'left'
      for (const step of steps) {
        ctx.fillStyle = '#000000'
        ctx.font = '13px system-ui, sans-serif'
        ctx.fillText(step, MARGIN, y + 20)
        y += 28
      }

      // ── Divider ──
      ctx.strokeStyle = '#999999'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(MARGIN, y)
      ctx.lineTo(CANVAS_W - MARGIN, y)
      ctx.stroke()
      y += dividerH

      // ── Column headers ──
      ctx.fillStyle = '#444444'
      ctx.font = 'bold 11px system-ui, sans-serif'
      ctx.textAlign = 'left'
      ctx.fillText('TICKET ID', MARGIN, y + 18)
      ctx.textAlign = 'right'
      ctx.fillText('PRICE', CANVAS_W - MARGIN, y + 18)
      y += ticketHeaderH

      // ── Ticket rows ──
      for (const ticket of this.claimData.tickets) {
        ctx.fillStyle = '#000000'
        ctx.font = '14px monospace'
        ctx.textAlign = 'left'
        ctx.fillText(ticket.id, MARGIN, y + 24)
        ctx.font = '14px system-ui, sans-serif'
        ctx.textAlign = 'right'
        ctx.fillText(`$${this.formatPrice(ticket.price)}`, CANVAS_W - MARGIN, y + 24)
        y += ticketRowH
        ctx.strokeStyle = '#cccccc'
        ctx.lineWidth = 1
        ctx.setLineDash([4, 4])
        ctx.beginPath()
        ctx.moveTo(MARGIN, y)
        ctx.lineTo(CANVAS_W - MARGIN, y)
        ctx.stroke()
        ctx.setLineDash([])
      }
      y += 8

      // ── Total band ──
      ctx.fillStyle = '#000000'
      ctx.fillRect(0, y, CANVAS_W, totalRowH)
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 18px system-ui, sans-serif'
      ctx.textAlign = 'left'
      ctx.fillText('TOTAL', MARGIN, y + totalRowH / 2 + 7)
      ctx.textAlign = 'right'
      ctx.fillText(`$${this.claimTotalDisplay}`, CANVAS_W - MARGIN, y + totalRowH / 2 + 7)
      y += totalRowH

      // ── Footer ──
      ctx.fillStyle = '#333333'
      ctx.font = '12px system-ui, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(`Purchased: ${purchaseTime}`, CANVAS_W / 2, y + 20)
      ctx.fillText(`Retailer: ${retailerId}`, CANVAS_W / 2, y + 38)
      ctx.fillStyle = '#666666'
      ctx.font = 'italic 11px system-ui, sans-serif'
      ctx.fillText('Keep this receipt until redemption is confirmed.', CANVAS_W / 2, y + 58)

      let dataUrl
      try {
        const bmpBytes = this._canvasToBmp2bit(canvas)
        const b64 = this._uint8ArrayToBase64(bmpBytes)
        dataUrl = `data:image/bmp;base64,${b64}`
      } catch (e) {
        console.error('[RECEIPT] BMP encoding failed:', e)
        throw e
      }

      try {
        window.parent.postMessage({ type: 'PRINT_RECEIPT', imageData: dataUrl }, '*')
      } catch (e) {
        // postMessage failure is non-fatal — still return the image
        console.warn('[RECEIPT] postMessage failed:', e)
      }

      return dataUrl
    },

    _canvasToBmp2bit(canvas) {
      const w = canvas.width
      const h = canvas.height
      const imageData = canvas.getContext('2d').getImageData(0, 0, w, h)
      const src = imageData.data

      // Palette: black, dark gray, light gray, white
      // Each row padded to 4-byte boundary; at 2 bits/px, 640px = 160 bytes/row (already aligned)
      const stride = Math.ceil(Math.ceil(w * 2 / 8) / 4) * 4
      const fileHeaderSize = 14
      const dibHeaderSize = 40
      const colorTableSize = 16 // 4 colors × 4 bytes
      const pixelDataOffset = fileHeaderSize + dibHeaderSize + colorTableSize
      const pixelDataSize = stride * h
      const fileSize = pixelDataOffset + pixelDataSize

      const buf = new ArrayBuffer(fileSize)
      const view = new DataView(buf)
      const out = new Uint8Array(buf)

      // BMP file header
      out[0] = 0x42; out[1] = 0x4d // 'BM'
      view.setUint32(2, fileSize, true)
      view.setUint32(6, 0, true)
      view.setUint32(10, pixelDataOffset, true)

      // BITMAPINFOHEADER
      view.setUint32(14, 40, true)
      view.setInt32(18, w, true)
      view.setInt32(22, h, true) // positive = bottom-up row order
      view.setUint16(26, 1, true)
      view.setUint16(28, 2, true) // 2 bits per pixel
      view.setUint32(30, 0, true) // BI_RGB, no compression
      view.setUint32(34, pixelDataSize, true)
      view.setUint32(38, 2835, true) // ~72 DPI x
      view.setUint32(42, 2835, true) // ~72 DPI y
      view.setUint32(46, 4, true)   // colors in table
      view.setUint32(50, 0, true)

      // Color table (RGBQUAD order: B, G, R, reserved)
      const paletteOffset = fileHeaderSize + dibHeaderSize
      const grays = [0, 85, 170, 255]
      for (let i = 0; i < 4; i++) {
        const g = grays[i]
        out[paletteOffset + i * 4] = g
        out[paletteOffset + i * 4 + 1] = g
        out[paletteOffset + i * 4 + 2] = g
        out[paletteOffset + i * 4 + 3] = 0
      }

      // Pixel data — rows stored bottom-to-top
      for (let row = 0; row < h; row++) {
        const dstRow = pixelDataOffset + (h - 1 - row) * stride
        for (let col = 0; col < w; col++) {
          const p = (row * w + col) * 4
          const lum = Math.round(0.299 * src[p] + 0.587 * src[p + 1] + 0.114 * src[p + 2])
          const idx = Math.min(3, lum >> 6) // 0-63→0, 64-127→1, 128-191→2, 192-255→3
          const bytePos = dstRow + (col >> 2)
          const shift = 6 - (col & 3) * 2
          out[bytePos] |= idx << shift
        }
      }

      return out
    },

    _uint8ArrayToBase64(bytes) {
      let binary = ''
      const chunk = 8192
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, Math.min(i + chunk, bytes.length)))
      }
      return btoa(binary)
    },

    doneClaim() {
      this.claimData = null
      this.cart = []
      this.devQrTaps = 0
      this.devReceiptTaps = 0
      this.devReceiptImage = null
      this.devReceiptError = null
      this.fetchAll()
    },

    devQrTap() {
      if (!this.isDev) return
      this.devQrTaps = Math.min(this.devQrTaps + 1, 5)
    },

    async devReceiptTap() {
      if (!this.isDev) return
      this.devReceiptTaps = Math.min(this.devReceiptTaps + 1, 5)
      if (this.devReceiptTaps === 5) {
        await this._runReceiptPreview()
      }
    },

    async devRegenerateReceipt() {
      if (!this.isDev) return
      this.devReceiptImage = null
      this.devReceiptError = null
      await this._runReceiptPreview()
    },

    async _runReceiptPreview() {
      this.devReceiptError = null
      try {
        this.devReceiptImage = await this.generateAndPostReceiptImage()
        if (!this.devReceiptImage) {
          this.devReceiptError = 'generateAndPostReceiptImage returned no data'
        }
      } catch (err) {
        console.error('[DEV] Receipt image generation failed:', err)
        this.devReceiptError = err?.message || String(err)
      }
    },

    devCopyQr() {
      if (!this.isDev || !this.claimData) return
      const text = this.claimData.qrValue
      // navigator.clipboard requires HTTPS; fall back to execCommand for plain HTTP
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(text)
          .catch(() => this.devCopyFallback(text))
      } else {
        this.devCopyFallback(text)
      }
    },

    devCopyFallback(text) {
      const el = document.createElement('textarea')
      el.value = text
      Object.assign(el.style, {
        position: 'fixed',
        top: '-9999px',
        left: '-9999px',
        opacity: '0',
      })
      document.body.appendChild(el)
      el.focus()
      el.select()
      try {
        document.execCommand('copy')
      } catch (_) {}
      document.body.removeChild(el)
    },

    // ── Selection ─────────────────────────────────────────────────────────
    selectGame(game) {
      this.selectedType = this.selectedType === game.type ? null : game.type
    },

    showCardInfo(game) {
      this.cardInfoGame = game
    },

    // ── Image ─────────────────────────────────────────────────────────────
    getImageSrc(data) {
      if (!data) return null
      if (data.startsWith('data:')) return data
      // Detect format from leading base64 chars (magic bytes)
      if (data.startsWith('/9j/')) return `data:image/jpeg;base64,${data}`
      if (data.startsWith('iVBOR')) return `data:image/png;base64,${data}`
      if (data.startsWith('R0lG')) return `data:image/gif;base64,${data}`
      if (data.startsWith('PHN2') || data.startsWith('PD94') || data.startsWith('Pz94'))
        return `data:image/svg+xml;base64,${data}`
      return `data:image/jpeg;base64,${data}`
    },

    // ── Helpers ───────────────────────────────────────────────────────────
    // Price from retail-monitor-be arrives as Italian locale currency string, e.g. "0,07 €".
    // Strip the symbol and whitespace, swap decimal comma for dot, then parse as float.
    parsePrice(raw) {
      const s = String(raw ?? '')
        .replace(/[€$£¥\s]/g, '')
        .replace(',', '.')
      const n = parseFloat(s)
      return isNaN(n) ? 0 : n
    },

    formatPrice(raw) {
      const n = this.parsePrice(raw)
      return n === 0 &&
        (raw === null || raw === undefined || String(raw).trim() === '')
        ? '—'
        : n.toFixed(2)
    },

    formatName(raw) {
      if (!raw) return 'Game'
      return raw
        .replace(/[-_]/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    },
  },
}
</script>

<style scoped>
/* ── Newton 2.0 palette ───────────────────────────────────────────────
   page-bg     #F6F9FC  ← page / main bg (light)
   primary-900 #04182f  ← deep bg (overlays, claim screen)
   primary-800 #072e5b  ← surface / panel bg (header, modals, cart)
   primary-700 #0a4180  ← borders / dividers (dark surfaces)
   primary-600 #0c51a1  ← CTA buttons / active fills
   primary-500 #4c88c3  ← accent text / borders / highlights
   primary-400 #8eb3d9  ← secondary labels
   blue-grey-400 #b3bde5 ← muted text (dark surfaces)
   slate-700   #334155  ← body text on light
   slate-500   #64748b  ← muted text on light
   border-light #e2e8f0 ← borders on light bg
   success-250 #4caf50  ← price pill (green = currency)
   success-500 #357939  ← live status
   error-500   #d0021b  ← offline status
   ──────────────────────────────────────────────────────────────────── */

/* ── Root ─────────────────────────────────────────────────────────────── */
.catalog-root {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #F6F9FC;
  color: #334155;
  font-family: 'Source Sans 3', 'Brightstar Sans', system-ui, sans-serif;
  overflow: hidden;
}

/* ── Header ──────────────────────────────────────────────────────────── */
.catalog-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background: #072e5b;
  border-bottom: 2px solid #4c88c3;
  padding: 0 24px;
  height: 64px;
  flex-shrink: 0;
  z-index: 10;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* ── Footer ──────────────────────────────────────────────────────────── */
.catalog-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #F6F9FC;
  border-top: 1px solid #e2e8f0;
  padding: 0 16px;
  height: 28px;
  flex-shrink: 0;
  z-index: 10;
}
.footer-terminal {
  display: flex;
  align-items: center;
  gap: 4px;
}
.footer-terminal-label {
  font-size: 8px;
  color: #64748b;
  letter-spacing: 2px;
  text-transform: uppercase;
}
.footer-terminal-id {
  font-size: 8px;
  color: #64748b;
  font-weight: 400;
}
.footer-live {
  display: flex;
  align-items: center;
}
.ws-dot {
  font-size: 8px;
  letter-spacing: 1px;
  font-weight: 500;
}
.ws-live {
  color: #4caf50;
}
.ws-dead {
  color: #ef9a9a;
}

/* Hidden debug tap zone — top-left corner */
.debug-tap-zone {
  position: fixed;
  top: 0;
  left: 0;
  width: 48px;
  height: 48px;
  z-index: 300;
  cursor: default;
  -webkit-tap-highlight-color: transparent;
}

/* Cart button in header */
.cart-btn {
  position: relative;
  background: #072e5b;
  border: 1px solid #0a4180;
  color: #fff;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.2s,
    border-color 0.2s;
}
.cart-btn:hover {
  background: #0c51a1;
  border-color: #0c51a1;
}
.cart-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  background: #0c51a1;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

/* ── Catalog grid ────────────────────────────────────────────────────── */
.catalog-wrap {
  flex: 1;
  overflow-y: auto;
}

.catalog-track {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
  padding: 20px 24px;
}

/* ── Game Card ───────────────────────────────────────────────────────── */
.game-card {
  position: relative;
  aspect-ratio: 1;
  border-radius: 20px;
  border: 2px solid #d0d9e8;
  cursor: pointer;
  overflow: hidden;
  transition:
    transform 0.2s,
    border-color 0.2s,
    box-shadow 0.2s;
}
.game-card:hover {
  transform: translateY(-6px) scale(1.02);
  border-color: #4c88c3;
  box-shadow: 0 12px 32px rgba(76, 136, 195, 0.35);
}
.card-active {
  border-color: #4c88c3 !important;
  box-shadow: 0 0 0 3px rgba(76, 136, 195, 0.4) !important;
}

.card-info-tap {
  position: absolute;
  top: 0;
  left: 0;
  width: 48px;
  height: 48px;
  z-index: 3;
  cursor: default;
  -webkit-tap-highlight-color: transparent;
}

.card-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.card-img-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e2e8f0;
  font-size: 64px;
  opacity: 0.3;
}

/* White floating bar at bottom */
.card-bottom-bar {
  position: absolute;
  bottom: 12px;
  left: 12px;
  right: 12px;
  z-index: 2;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50px;
  height: 44px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 14px;
  backdrop-filter: blur(6px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
}
.card-bar-spacer {
  /* left spacer to balance the right column for centering */
}
.card-price-pill {
  background: #4caf50;
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.3px;
  padding: 5px 18px;
  border-radius: 50px;
  white-space: nowrap;
}
.card-bar-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}
.card-ticket-count {
  font-size: 12px;
  font-weight: 700;
  color: #535158;
  white-space: nowrap;
}

/* ── Game Detail Modal ──────────────────────────────────────────────── */
.game-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.game-modal-card {
  position: relative;
  width: 100%;
  max-width: 660px;
  background: linear-gradient(160deg, #072e5b 0%, #04182f 100%);
  border: 2px solid #0c51a1;
  border-radius: 20px;
  padding: 36px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.7);
}

.expanded-close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(12, 81, 161, 0.15);
  border: 1px solid #4c88c3;
  color: #4c88c3;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}
.expanded-close:hover {
  background: #0c51a1;
  color: #fff;
  border-color: #0c51a1;
}

.game-modal-body {
  display: flex;
  flex-direction: row;
  gap: 32px;
  align-items: center;
}

.expanded-img-col {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  max-height: 320px;
}
.expanded-img {
  max-width: 100%;
  max-height: 320px;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
}
.expanded-img-placeholder {
  font-size: 80px;
  opacity: 0.2;
}

.expanded-detail-col {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  width: 220px;
}

.expanded-avail-badge {
  background: rgba(12, 81, 161, 0.2);
  border: 1px solid #4c88c3;
  color: #8eb3d9;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 20px;
}
.expanded-avail-count {
  font-size: 13px;
  color: #8eb3d9;
  font-weight: 600;
  margin: 0;
  text-align: center;
}
.expanded-name {
  font-size: 20px;
  font-weight: 800;
  font-family: 'Brightstar Sans', 'Source Sans 3', system-ui, sans-serif;
  color: #fff;
  text-align: center;
  margin: 0;
}
.expanded-price {
  font-size: 32px;
  font-weight: 900;
  color: #fff;
  margin: 0;
}

.btn-add-to-cart {
  background: #0c51a1;
  border: none;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  padding: 14px 20px;
  border-radius: 10px;
  cursor: pointer;
  width: 100%;
  transition:
    background 0.2s,
    opacity 0.2s;
}
.btn-add-to-cart:hover:not(:disabled) {
  background: #0a4180;
}
.btn-add-to-cart:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  background: #072e5b;
}

.cart-feedback {
  font-size: 24px;
  color: #4caf50;
  text-align: center;
  margin: 0;
}

/* ── Cart panel ──────────────────────────────────────────────────────── */
.cart-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 49;
}

.cart-panel {
  position: fixed;
  top: 64px;
  right: 0;
  bottom: 0;
  width: 360px;
  max-width: 90vw;
  background: linear-gradient(160deg, #072e5b 0%, #04182f 100%);
  border-left: 2px solid #0c51a1;
  z-index: 50;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 40px rgba(0, 0, 0, 0.6);
}

.cart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px;
  border-bottom: 1px solid #0a4180;
  flex-shrink: 0;
}
.cart-title {
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.5px;
}

.cart-items {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cart-empty {
  color: #7e7d82;
  font-size: 16px;
  text-align: center;
  padding: 32px 16px;
}

.cart-item {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #072e5b;
  border-radius: 10px;
  padding: 10px;
}
.cart-item-thumb {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
}
.cart-item-thumb-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  background: #04182f;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.cart-item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.cart-item-name {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
}
.cart-item-id {
  font-size: 13px;
  color: #7e7d82;
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cart-item-price {
  font-size: 16px;
  font-weight: 700;
  color: #4c88c3;
}

.cart-item-qty-controls {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.cart-item-qty {
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  min-width: 16px;
  text-align: center;
}
.cart-remove-btn {
  flex-shrink: 0;
  background: rgba(239, 83, 80, 0.15);
  border: 1px solid #ef5350;
  color: #ef9a9a;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.cart-remove-btn:hover {
  background: #ef5350;
  color: #fff;
}
.cart-add-btn {
  flex-shrink: 0;
  background: rgba(66, 165, 245, 0.15);
  border: 1px solid #42a5f5;
  color: #90caf9;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.cart-add-btn:hover {
  background: #42a5f5;
  color: #fff;
}

.cart-footer {
  border-top: 1px solid #0a4180;
  padding: 16px 20px 20px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.cart-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.cart-total-lbl {
  font-size: 16px;
  color: #b3bde5;
  font-weight: 600;
}
.cart-total-val {
  font-size: 28px;
  font-weight: 900;
  color: #fff;
}

.cart-error {
  color: #ef9a9a;
  font-size: 15px;
  text-align: center;
}

.btn-checkout {
  background: #0c51a1;
  border: none;
  color: #fff;
  font-size: 20px;
  font-weight: 800;
  padding: 14px;
  border-radius: 10px;
  cursor: pointer;
  width: 100%;
  transition:
    background 0.2s,
    opacity 0.2s;
}
.btn-checkout:hover:not(:disabled) {
  background: #0a4180;
}
.btn-checkout:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ── Claim screen ────────────────────────────────────────────────────── */
.claim-screen {
  position: fixed;
  inset: 0;
  background: #04182f;
  color: #fff;
  z-index: 100;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.claim-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #072e5b;
  border-bottom: 2px solid #4c88c3;
  padding: 0 24px;
  height: 64px;
  flex-shrink: 0;
}
.claim-header-title {
  font-size: 16px;
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.5px;
}
.claim-close-btn {
  font-size: 14px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #0a4180;
  color: #b3bde5;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}
.claim-close-btn:hover {
  background: #072e5b;
  color: #fff;
}

.claim-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px 40px;
  gap: 16px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
}

.claim-instruction {
  font-size: 14px;
  color: #b3bde5;
  text-align: center;
  margin: 0;
  letter-spacing: 0.3px;
}

.claim-qr-wrap {
  background: #fff;
  padding: 16px;
  border-radius: 16px;
  line-height: 0;
  box-shadow: 0 8px 32px rgba(12, 81, 161, 0.3);
  min-width: 312px;
  min-height: 312px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dev-tap-hint {
  font-size: 11px;
  color: rgba(76, 136, 195, 0.65);
  margin: 4px 0 0;
  font-style: italic;
  text-align: center;
}

.dev-qr-reveal {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: rgba(12, 81, 161, 0.1);
  border: 1px dashed rgba(76, 136, 195, 0.5);
  border-radius: 10px;
  padding: 12px 16px;
  max-width: 320px;
  width: 100%;
}

.dev-qr-label {
  font-size: 11px;
  color: #4c88c3;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.dev-qr-code {
  font-family: monospace;
  font-size: 11px;
  color: #2d2d2d;
  background: #e4ecf6;
  border-radius: 6px;
  padding: 8px 10px;
  word-break: break-all;
  width: 100%;
  user-select: text;
  cursor: text;
}

.dev-copy-btn {
  background: #0c51a1;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 18px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.dev-copy-btn:hover {
  background: #0a4180;
}

.dev-receipt-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  background: rgba(12, 81, 161, 0.1);
  border: 1px dashed rgba(76, 136, 195, 0.5);
  border-radius: 10px;
  padding: 14px 16px;
  max-width: 340px;
  width: 100%;
}

.dev-receipt-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
}

.dev-receipt-img {
  width: 100%;
  border-radius: 6px;
  border: 1px solid rgba(76, 136, 195, 0.3);
  display: block;
}

.dev-receipt-error {
  font-size: 12px;
  color: #ef9a9a;
  font-family: monospace;
  word-break: break-all;
  text-align: center;
  margin: 0;
}

.claim-or-label {
  font-size: 12px;
  color: #535158;
  margin: 0;
}

.claim-print-label {
  font-size: 12px;
  color: #7e7d82;
  margin: 0;
  text-align: center;
}

.claim-ticket-list {
  width: 100%;
  background: #072e5b;
  border-radius: 24px;
  overflow: hidden;
}
.claim-ticket-row {
  display: flex;
  align-items: center;
  padding: 16px 26px;
  border-bottom: 1px solid #04182f;
  gap: 20px;
}
.claim-ticket-row:last-child {
  border-bottom: none;
}
.claim-ticket-id {
  flex: 1;
  font-size: 11px;
  color: #4c88c3;
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.claim-ticket-price {
  font-size: 13px;
  font-weight: 700;
  color: #4c88c3;
  flex-shrink: 0;
}

.claim-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 26px;
  border-top: 2px solid #0a4180;
  background: #04182f;
}
.claim-total-lbl {
  font-size: 13px;
  color: #4c88c3;
  font-weight: 600;
}
.claim-total-val {
  font-size: 18px;
  font-weight: 900;
  color: #4c88c3;
}

.claim-actions {
  display: flex;
  gap: 12px;
  width: 100%;
}
.btn-print {
  flex: 1;
  background: #072e5b;
  border: 1px solid #0a4180;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  padding: 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s;
}
.btn-print:hover {
  background: #0c51a1;
  border-color: #0c51a1;
}
.btn-done-claim {
  flex: 1;
  background: #0c51a1;
  border: none;
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  padding: 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s;
}
.btn-done-claim:hover {
  background: #0a4180;
}

/* ── Info / Debug overlay ────────────────────────────────────────────── */
.info-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
}
.info-card {
  background: #072e5b;
  border: 1px solid #0a4180;
  border-radius: 16px;
  padding: 24px;
  min-width: 320px;
  max-width: 420px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.6);
}
.info-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
}
.overlay-close {
  background: none;
  border: 1px solid #0a4180;
  color: #b3bde5;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.overlay-close:hover {
  background: #ef5350;
  color: #fff;
  border-color: #ef5350;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #0a4180;
  gap: 8px;
}
.info-row-sm {
  padding: 3px 0;
}
.info-lbl {
  font-size: 12px;
  color: #8eb3d9;
  flex-shrink: 0;
}
.info-val {
  font-size: 13px;
  color: #fff;
  text-align: right;
}
.info-mono {
  font-family: monospace;
  font-size: 12px;
  color: #4c88c3;
  text-align: right;
  word-break: break-all;
}
.val-ok {
  color: #4caf50;
  font-size: 13px;
  font-weight: 600;
}
.val-err {
  color: #ef9a9a;
  font-size: 13px;
  font-weight: 600;
}
.info-divider {
  height: 1px;
  background: #0a4180;
  margin: 10px 0;
}
.info-error {
  color: #ef9a9a;
  font-size: 12px;
  margin: 8px 0;
}

.info-refresh-btn {
  display: block;
  width: 100%;
  margin-top: 16px;
  padding: 10px;
  background: #0c51a1;
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;
}
.info-refresh-btn:hover {
  background: #0a4180;
}

/* ── Loading / empty ─────────────────────────────────────────────────── */
.state-message {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #334155;
  font-size: 16px;
}
.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #d0d9e8;
  border-top-color: #4c88c3;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Transitions ─────────────────────────────────────────────────────── */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.modal-pop-enter-active {
  transition:
    opacity 0.22s ease,
    transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.modal-pop-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease-in;
}
.modal-pop-enter-from,
.modal-pop-leave-to {
  opacity: 0;
  transform: scale(0.94);
}

.slide-right-enter-active {
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}
.slide-right-leave-active {
  transition: transform 0.28s ease-in;
}
.slide-right-enter-from,
.slide-right-leave-to {
  transform: translateX(100%);
}

/* ── Print ───────────────────────────────────────────────────────────── */
@media print {
  .catalog-header,
  .info-overlay,
  .game-modal-backdrop,
  .cart-backdrop,
  .cart-panel,
  .catalog-wrap,
  .catalog-track,
  .state-message {
    display: none !important;
  }

  .claim-screen {
    position: static;
    background: #ffffff;
    color: #000000;
  }
  .claim-print-hide {
    display: none !important;
  }
  .claim-instruction {
    color: #333;
  }
  .claim-print-label {
    color: #333;
    font-size: 14px;
  }
  .claim-ticket-list {
    background: #f5f5f5;
    border: 1px solid #ddd;
  }
  .claim-ticket-id {
    color: #000;
  }
  .claim-ticket-price,
  .claim-total-val {
    color: #333;
  }
  .claim-total-row {
    background: #eee;
  }
  .claim-qr-wrap {
    box-shadow: none;
    border: 1px solid #ddd;
  }
}
</style>
